import type { TDateISO } from "~/@types/TDateISO";
import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";

import encodeListFilterValues, { readListFilterValues } from "~/helpers/listFilterValues";

export const LABOR_TASKS_SOURCE_KIND = "laborTasks";

export const LABOR_TASK_TARGET_KIND = "laborTask";

export const LABOR_TASK_LIST_TARGET_KIND = "laborTaskList";

export const LABOR_TASKS_BASE_PATH = "/produce/labor/tasks";

export const LABOR_TASKS_DOCK_PANEL_ID = "labor-tasks";

const LABOR_TASK_ROUTE_PATTERN = /^\/produce\/labor\/tasks\/([^/?#]+)$/;

export interface LaborTaskReassignDraft {
	newAssignedTo: string;
	newDueDate: TDateISO | null;
	savedAssignedTo: string;
}

export interface LaborTasksSourceCheckpoint {
	reassignDraft: LaborTaskReassignDraft | null;
	searchText: string;
	taskAssignmentId: string;
	taskLabel: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const isReassignDraft = (value: unknown): value is LaborTaskReassignDraft =>
	isRecord(value) &&
	typeof value.newAssignedTo === "string" &&
	(value.newDueDate === null || typeof value.newDueDate === "string") &&
	typeof value.savedAssignedTo === "string";

export const isLaborTasksSourceCheckpoint = (value: unknown): value is LaborTasksSourceCheckpoint =>
	isRecord(value) &&
	(value.reassignDraft === null || isReassignDraft(value.reassignDraft)) &&
	typeof value.searchText === "string" &&
	typeof value.taskAssignmentId === "string" &&
	typeof value.taskLabel === "string";

export const isLaborTasksRoute = (pathname: string): boolean =>
	pathname === LABOR_TASKS_BASE_PATH || pathname.startsWith(`${LABOR_TASKS_BASE_PATH}/`);

export const matchLaborTaskReceiverRoute = (pathname: string): ContinuationTarget | null => {
	const match = LABOR_TASK_ROUTE_PATTERN.exec(pathname);
	if (!match) return null;
	try {
		return { targetId: decodeURIComponent(match[1]), targetKind: LABOR_TASK_TARGET_KIND };
	} catch {
		return null;
	}
};

export const laborTaskReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: ({ targetId }) => `${LABOR_TASKS_BASE_PATH}/${encodeURIComponent(targetId)}`,
	matchesRoute: matchLaborTaskReceiverRoute
};

export interface LaborTaskListScope {
	taskAssignmentIds: string[];
}

export const buildLaborTaskListTarget = (
	listId: string,
	{ taskAssignmentIds }: LaborTaskListScope
): ContinuationTarget => ({
	listFilters: { taskAssignmentIds: encodeListFilterValues(taskAssignmentIds) },
	targetId: listId,
	targetKind: LABOR_TASK_LIST_TARGET_KIND
});

export const readLaborTaskListScope = (listFilters: Record<string, string> | undefined): LaborTaskListScope | null => {
	const taskAssignmentIds = readListFilterValues(listFilters?.taskAssignmentIds);
	return taskAssignmentIds ? { taskAssignmentIds } : null;
};

export const laborTaskListReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: () => LABOR_TASKS_BASE_PATH,
	matchesRoute: () => null
};

const buildLaborTasksEditorKey = (taskAssignmentId: string): string =>
	`${LABOR_TASKS_DOCK_PANEL_ID}:${taskAssignmentId}`;

export default buildLaborTasksEditorKey;
